import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiSearch, FiShoppingCart, FiUser, FiMapPin, FiStar, FiClock } from 'react-icons/fi';
import api from '@/services/api';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/formatters';
import ProductCard from '@/components/cards/ProductCard';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const PublicHomePage = () => {
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { addToCart, getTotalItems, cart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [storesRes, productsRes] = await Promise.all([
        api.get('/public/stores'),
        api.get('/public/products?limit=20')
      ]);
      setStores(storesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/busca?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product.store_id, product);
  };

  const handleCheckout = () => {
    if (!user) {
      navigate('/login?redirect=/cliente/carrinho');
    } else {
      navigate('/cliente/carrinho');
    }
  };

  const filteredProducts = searchQuery 
    ? products.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : products;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            {/* Logo - Link para página inicial */}
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <span className="text-xl font-bold">BARUERI</span>
              <svg viewBox="0 0 60 100" className="w-5 h-8">
                <defs>
                  <linearGradient id="boltGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FFED4A" />
                    <stop offset="50%" stopColor="#FFD700" />
                    <stop offset="100%" stopColor="#FFA500" />
                  </linearGradient>
                </defs>
                <path d="M45 0 L25 40 L40 40 L20 70 L35 70 L10 100 L50 60 L35 60 L55 30 L40 30 L60 0 Z" fill="url(#boltGrad)"/>
              </svg>
              <span className="font-bold text-[#FFD700]">FLASH</span>
            </Link>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleCheckout}
                className="relative p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <FiShoppingCart className="text-xl" />
                {getTotalItems() > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#DC143C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {getTotalItems()}
                  </span>
                )}
              </button>
              
              {user ? (
                <button
                  onClick={() => navigate(`/${user.user_type}`)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <FiUser className="text-xl" />
                </button>
              ) : (
                <Link
                  to="/login"
                  className="px-4 py-2 bg-[#DC143C] hover:bg-[#B01030] rounded-lg text-sm font-medium transition-colors"
                >
                  Entrar
                </Link>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="relative">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar produtos em Barueri..."
              className="w-full pl-12 pr-4 py-3 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#DC143C]"
            />
          </form>

          <div className="flex items-center gap-1 mt-3 text-white/70 text-sm">
            <FiMapPin className="text-xs" />
            <span>📍 Barueri, SP</span>
          </div>
        </div>
      </header>

      {/* Stores Section */}
      {stores.length > 0 && (
        <section className="bg-white shadow-sm mb-4">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Lojas</h2>
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {stores.map((store) => (
                <Link
                  key={store.id}
                  to={`/loja/${store.slug}`}
                  className="flex-shrink-0 w-28 text-center group"
                >
                  <div className="w-20 h-20 mx-auto rounded-xl overflow-hidden bg-gray-100 group-hover:ring-2 group-hover:ring-[#DC143C] transition-all">
                    {store.logo ? (
                      <img src={store.logo} alt={store.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-gray-400">
                        {store.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs font-medium text-gray-700 truncate">{store.name}</p>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <span className={`w-2 h-2 rounded-full ${store.is_open ? 'bg-green-500' : 'bg-orange-500'}`} />
                    <span className="text-[10px] text-gray-500">
                      {store.is_open ? 'Aberta' : 'Fechada'}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Products Section */}
      <main className="max-w-7xl mx-auto px-4 py-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          {searchQuery ? `Resultados para "${searchQuery}"` : 'Produtos em Destaque'}
        </h2>

        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Nenhum produto encontrado</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((product) => (
              <div key={product.id} className="relative">
                <Link 
                  to={`/loja/${product.store_slug}`}
                  className="absolute top-2 right-2 z-10 text-[10px] bg-black/50 text-white px-2 py-1 rounded-full hover:bg-black/70"
                >
                  Ver loja
                </Link>
                <ProductCard
                  product={product}
                  onAddToCart={() => handleAddToCart(product)}
                  onClick={() => navigate(`/loja/${product.store_slug}`)}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Floating Cart Button */}
      {getTotalItems() > 0 && (
        <div className="fixed bottom-4 left-4 right-4 max-w-md mx-auto">
          <button
            onClick={handleCheckout}
            className="w-full py-4 bg-[#DC143C] text-white rounded-2xl font-semibold shadow-lg flex items-center justify-center gap-3 hover:bg-[#B01030] transition-colors"
          >
            <FiShoppingCart className="text-xl" />
            <span>Ver Carrinho ({getTotalItems()} {getTotalItems() === 1 ? 'item' : 'itens'})</span>
          </button>
        </div>
      )}

      {/* CTA for non-logged users */}
      {!user && (
        <div className="fixed bottom-20 left-4 right-4 max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-2">
              Faça login para finalizar sua compra ou acompanhar pedidos
            </p>
            <div className="flex gap-2">
              <Link
                to="/login"
                className="flex-1 py-2 text-center bg-[#DC143C] text-white rounded-lg text-sm font-medium hover:bg-[#B01030]"
              >
                Entrar
              </Link>
              <Link
                to="/register"
                className="flex-1 py-2 text-center border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Criar conta
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicHomePage;
